/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * BG's masthead BLOCK CHROME — the bordered, inset box whose own `border-b` is
 * the black rule that closes BG's heading region, on BOTH of BG's listing
 * surfaces.
 *
 * ONE COMPONENT FOR TWO SURFACES BECAUSE LIVE USES ONE BLOCK FOR TWO SURFACES.
 * The class string is byte-identical in the two captures, so this is a shared
 * chrome with different slots filled — not a concept forked into two files:
 *
 *   SRP  .reference/captures/bg-search-m-375/App.tsx:146
 *        border-b box-content text-CENTER ml-[16px] mr-[16px] border-black
 *        text-[12px] leading-[18px] min-h-[93px]
 *   PLP  .reference/captures/bg-plp-m-440/App.tsx:120
 *        border-b box-content text-LEFT   ml-[16px] mr-[16px] border-black
 *        text-[12px] leading-[18px] min-h-[93px]
 *
 * `align` is the only difference between them, and the SLOTS differ: the SRP
 * fills heading + count; the PLP fills heading + truncated description with its
 * `More` control + count + the subcategory tile band (that band is the block's
 * LAST CHILD in the capture, which is why live's rule paints below the tile
 * labels rather than above them).
 *
 * The outer wrapper's vertical space is live's and differs per surface
 * (`mt-[54px] mb-[15px]` on the SRP, `mt-[20px] mb-[20px]` on the PLP), so it is
 * a prop rather than a token baked in here. Values themselves are phase-2 work —
 * this component takes the class from its caller and invents no number.
 *
 * TOKEN NAMES SAY `srp` AND COVER BOTH SURFACES. The `--bg-srp-masthead-*` set
 * in `src/theme/tokens/brand-bg/search.css` was named when only the SRP mounted
 * this block; the PLP capture then produced the same measurements for the same
 * box. Renaming the set is a token-layer change and is reported rather than
 * smuggled in here.
 */
export function BgMastheadBlock({
    align,
    spacingClassName,
    testId,
    children,
}: {
    align: 'center' | 'left';
    /** Live's own outer vertical space for this surface, supplied by the caller. */
    spacingClassName: string;
    testId: string;
    children: ReactNode;
}): ReactElement {
    return (
        <div data-testid={testId} className={spacingClassName}>
            <div
                data-testid={`${testId}-block`}
                className={cn(
                    'box-content min-h-(--bg-srp-masthead-min-height)',
                    'border-b-(length:--bg-srp-masthead-rule-width)',
                    '[border-bottom-color:var(--bg-srp-masthead-rule-color)]',
                    'mx-(--bg-srp-masthead-inset-inline)',
                    'text-(length:--bg-srp-masthead-count-font-size) leading-(--bg-srp-masthead-count-line-height)',
                    align === 'center' ? 'text-center' : 'text-left'
                )}>
                {children}
            </div>
        </div>
    );
}

/**
 * BG's SRP masthead — the centred query heading, the centred result count, and
 * the black rule that closes the block.
 *
 * THIS IS A BG BAND, NOT A SKINNED `ProductListingHeader`. Live NM's mobile SRP
 * renders a left-aligned `Results for "Dress"` line and no rule at all
 * (.reference/captures/search-m-390/App.tsx:212-216); live BG renders this.
 * Two different things, so two components — see `SearchUx.centredMasthead`.
 *
 * STRUCTURE, read in document order from
 * `.reference/captures/bg-search-m-375/App.tsx` at :146 and the ~10 lines above
 * it, and matched against the owner's browser capture
 * `.reference/live-shots/bg-search-390.png`:
 *
 *   div.mt-[54px].mb-[15px]                              ← the band's own space
 *     div.border-b.box-content.text-center.ml-[16px].mr-[16px].border-black
 *        .text-[12px].leading-[18px].min-h-[93px]        ← THE RULE LIVES HERE
 *       h1.font-light.text-center.uppercase.mt-[7px].mb-[4px]
 *          .text-[rgb(51,_51,_51)].text-[30px].leading-[32.1px]  >dress<
 *       span.text-center                                 >3,742 Results<
 *
 * Two things a side-by-side gets wrong about this band, both settled above:
 *
 *  1. THE RULE IS NOT A SEPARATE BAND. It is `border-b border-black` on the
 *     block that holds the heading and the count, which is why it is inset from
 *     the frame by live's own `ml/mr-[16px]` rather than running edge to edge.
 *     An `<hr>` sibling would have to re-derive that inset from nothing.
 *  2. THE HEADING IS UPPERCASED BY CSS, NOT BY THE DATA. Live's text node is
 *     the raw lowercase query `dress`; `uppercase` is on the element. So the
 *     accessible name and the URL round-trip keep the shopper's own casing.
 *
 * Every value is a `--bg-srp-masthead-*` token in
 * `src/theme/tokens/brand-bg/search.css`. The component is mounted only under
 * `SearchUx.centredMasthead`, which no brand but BG sets, so the tokens live in
 * BG's overlay alone rather than carrying neutral restatements no document
 * would ever resolve.
 *
 * THE PROP IS THE HEADING STRING, NOT THE RAW QUERY, and the name is load
 * bearing. A query-less SRP is a routine shopper state on both brands — the
 * navigation document points the Sale silo at `/search?refine=c_isSale%3Dtrue`
 * and every designer on the A-Z index links to a query-less search — so this
 * band renders with no query on three reachable URL shapes: bare `/search`,
 * one refinement, and several. Handed `searchTerm` it painted `<h1></h1>` on
 * all three, and because BG renders no shared results header the page then had
 * no heading in the accessibility tree at all. The route computes the guarded
 * string (the query when there is one, the sole refinement's FACET label when
 * there is not, the plain search title otherwise) and passes it here; that
 * guard already existed and had only ever been wired to the shared header.
 *
 * This does NOT change the query case. BG sets `quotedResultsHeading: false`,
 * so the guarded string IS the raw query verbatim when one exists, and live's
 * lowercase text node is preserved exactly as the note above requires.
 */
export default function BgSearchMasthead({ heading, total }: { heading: string; total: number }): ReactElement {
    const { t } = useTranslation('search');

    return (
        <BgMastheadBlock
            align="center"
            spacingClassName="mt-(--bg-srp-masthead-space-top) mb-(--bg-srp-masthead-space-bottom)"
            testId="bg-search-masthead">
            <h1
                data-testid="bg-search-masthead-title"
                className="mt-(--bg-srp-masthead-title-space-top) mb-(--bg-srp-masthead-title-space-bottom) uppercase [font-weight:var(--bg-srp-masthead-title-weight)] text-(length:--bg-srp-masthead-title-font-size) leading-(--bg-srp-masthead-title-line-height) text-(--bg-srp-masthead-title-color) tracking-(--bg-srp-masthead-title-letter-spacing)">
                {heading}
            </h1>
            <span data-testid="bg-search-masthead-count">
                {t('bgResultsCount', {
                    defaultValue: '{{count, number}} Results',
                    defaultValue_one: '{{count, number}} Result',
                    defaultValue_other: '{{count, number}} Results',
                    count: total,
                })}
            </span>
        </BgMastheadBlock>
    );
}
